"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { gsap } from "gsap";
import { TerminalIcon } from "@/components/app/TerminalIcon";
import { ProductPreview } from "../ProductPreview";
import { presentationAt, PRESENTATION_DURATION } from "./presentation";
import d from "./shell-example.module.css";
import s from "./cloud-shell.module.css";
import { useI18n } from "@/lib/i18n/I18nProvider";

/** A film of the dashboard workflow. Play/pause is its only interaction. */
export function CloudShellDemo() {
  const root = useRef<HTMLDivElement>(null);
  const terminal = useRef<HTMLPreElement>(null);
  const tween = useRef<gsap.core.Tween | null>(null);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(true);
  const t = useI18n().t.shellDemo;
  const [visible, setVisible] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const [width, setWidth] = useState(990);
  const frame = presentationAt(time);
  const compact = width < 760;
  const paneWidth = compact ? width : Math.min(440, Math.max(320, width * 0.4));

  useEffect(() => {
    const clock = { time: 0 };
    tween.current = gsap.to(clock, {
      time: PRESENTATION_DURATION,
      duration: PRESENTATION_DURATION,
      ease: "none",
      repeat: -1,
      paused: true,
      onUpdate: () => setTime(Math.floor(clock.time * 25) / 25),
    });
    const motion = matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotion = () => {
      setPlaying(!motion.matches);
      if (motion.matches) {
        tween.current?.time(13);
        setTime(13);
      }
    };
    const updateVisibility = () => setPageVisible(!document.hidden);
    updateMotion();
    updateVisibility();
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.15 },
    );
    const resize = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width),
    );
    if (root.current) {
      observer.observe(root.current);
      const consoleFrame = root.current.querySelector(".ed-console");
      if (consoleFrame) resize.observe(consoleFrame);
    }
    motion.addEventListener("change", updateMotion);
    document.addEventListener("visibilitychange", updateVisibility);
    return () => {
      tween.current?.kill();
      observer.disconnect();
      resize.disconnect();
      motion.removeEventListener("change", updateMotion);
      document.removeEventListener("visibilitychange", updateVisibility);
    };
  }, []);
  useEffect(() => {
    if (playing && visible && pageVisible) tween.current?.play();
    else tween.current?.pause();
  }, [playing, visible, pageVisible]);
  useEffect(() => {
    if (terminal.current)
      terminal.current.scrollTop = terminal.current.scrollHeight;
  }, [frame.transcript]);

  useEffect(() => {
    const element = terminal.current;
    if (!element) return;
    const scroll = () => {
      element.scrollTop = element.scrollHeight;
    };
    const observer = new ResizeObserver(scroll);
    observer.observe(element);
    void document.fonts.ready.then(scroll);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={root}
      className={d.root}
      data-shell-example
      data-time={time.toFixed(2)}
      data-phase={frame.phase}
      data-compact={compact}
      data-playing={playing}
      style={
        {
          "--dock": frame.dock,
          "--pane-width": `${paneWidth}px`,
        } as CSSProperties
      }
    >
      <ProductPreview
        cloudShell={{
          toolbar: (
            <button
              type="button"
              aria-label={playing ? t.pauseLabel : t.playLabel}
              onClick={() => setPlaying((value) => !value)}
            >
              {playing ? `Ⅱ ${t.pause}` : `▷ ${t.play}`}
            </button>
          ),
          trigger: (
            <span
              className={d.trigger}
              data-active={frame.dock > 0}
              aria-hidden="true"
            >
              <TerminalIcon size={20} />
            </span>
          ),
          panel: (
            <div className={d.track} aria-hidden={frame.dock === 0}>
              <div className={d.pane}>
                <div className={d.shellHeader}>
                  <div>
                    <h3>Cloud Shell</h3>
                    <span className={d.connection}>
                      <i />
                      {frame.phase === "starting" ? t.starting : t.connected}
                    </span>
                  </div>
                  <span className={d.chrome} aria-hidden="true">
                    ↻<span>···</span>×
                  </span>
                </div>
                {frame.phase === "starting" && (
                  <div className={d.boot} aria-hidden="true">
                    {t.boot.map(
                      (label, i) => (
                        <span key={label} data-ready={time >= 1.4 + i * 0.24}>
                          <i />
                          {label}
                        </span>
                      ),
                    )}
                  </div>
                )}
                <pre
                  ref={terminal}
                  className={d.output}
                  aria-label={t.output}
                  aria-live="off"
                >
                  <code>
                    <span className={d.welcome}>
                      {t.welcome}
                    </span>
                    {"\n\n"}
                    {frame.transcript}
                    <span className={d.cursor} aria-hidden="true" />
                  </code>
                </pre>
                <div className={d.sessionFooter}>
                  <span aria-hidden="true">▸</span> Session details{" "}
                  <span>Example session</span>
                </div>
              </div>
            </div>
          ),
        }}
      />
      <p className={s.srOnly}>
        Animated example: Cloud Shell opens beside the dashboard, types a help
        command and lists sample services. No real session is started.
      </p>
      <noscript>
        <p className={d.note}>
          The animated preview needs JavaScript. Read the{" "}
          <Link href="/docs/cloud-shell#commands">Cloud Shell guide</Link> for
          commands and instructions.
        </p>
      </noscript>
    </div>
  );
}

export function CopyCode({ code }: { code: string }) {
  const [notice, setNotice] = useState("");
  return (
    <div className={s.codeBlock}>
      <pre>
        <code>{code}</code>
      </pre>
      <button
        type="button"
        aria-label={`Copy ${code.split("\n")[0]}`}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(code);
            setNotice("Copied.");
          } catch {
            setNotice("Copy unavailable. Select the command to copy it.");
          }
        }}
      >
        Copy code
      </button>
      <span className={s.copyNotice} role="status">
        {notice}
      </span>
    </div>
  );
}

export function GuideTopics({
  topics,
}: {
  topics: { slug: string; title: string }[];
}) {
  const root = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const media = matchMedia("(min-width: 801px)");
    const update = () => {
      if (root.current) root.current.open = media.matches;
    };
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  return (
    <details ref={root}>
      <summary>Documentation</summary>
      <nav aria-label="Documentation topics">
        {topics.map((t) => (
          <Link
            key={t.slug}
            href={`/docs/${t.slug}`}
            aria-current={t.slug === "cloud-shell" ? "page" : undefined}
          >
            {t.title}
          </Link>
        ))}
      </nav>
    </details>
  );
}
